// Generated by GitHub Copilot

import { InteractionRequiredAuthError } from '@azure/msal-browser';
import msalInstance from './msalInstance';
import { loginRequest, ssoEnabled } from './authConfig';

/**
 * acquireApiToken — non-hook token helper for the Client Stories API.
 *
 * Returns the MSAL **id_token** for the current account. The id_token's `aud`
 * is this app's own client id, it is signed with the standard Entra keys and
 * carries no Graph header `nonce`, so the backend can verify it via JWKS
 * (RS256 + audience + issuer + exp) with no custom API scope / "Expose an API"
 * configuration. (An access token acquired for Graph scopes has `aud` = Graph
 * and is deliberately not verifiable by this API — see clientstories/auth.)
 *
 * Silently refreshes from the MSAL cache, falling back to an interactive
 * redirect if interaction is required (MFA, consent, expired session). Safe to
 * call from module-level helpers (DBStories' apiFetch/apiDownload) that cannot
 * use React hooks.
 *
 * When SSO is disabled (local dev), returns null so callers skip the
 * Authorization header — mirrors AlphaSenseTokenContext's no-SSO passthrough.
 *
 * @returns {Promise<string|null>} The raw id_token, or null when SSO is off.
 * @throws {Error} If no active account is available, or token acquisition fails
 *   for a reason other than InteractionRequired (interactive redirect navigates
 *   away, so this also throws to halt further execution).
 */
export async function acquireApiToken() {
  if (!ssoEnabled) return null;

  const activeAccount = msalInstance.getActiveAccount();
  if (!activeAccount) {
    throw new Error('[acquireApiToken] No active account — user must sign in first.');
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: activeAccount,
    });
    return response.idToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Silent acquisition failed — interaction needed. Redirect navigates away.
      msalInstance.acquireTokenRedirect({
        ...loginRequest,
        account: activeAccount,
      });
      throw err;
    }
    throw err;
  }
}
